import { Link, useLocation } from "@tanstack/react-router";
import { LayoutDashboard, Sparkles, BookOpen, Users, UserRound } from "lucide-react";
import { hapticSelection } from "@/lib/native-runtime";

type Tab = {
  to: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  match: (p: string) => boolean;
};

const TABS: Tab[] = [
  {
    to: "/dashboard",
    label: "Dashboard",
    icon: LayoutDashboard,
    match: (p) => p === "/dashboard",
  },
  {
    to: "/my-healing-journey",
    label: "Practice",
    icon: Sparkles,
    match: (p) =>
      /^\/(my-healing-journey|how-are-you-feeling|breathing|grounding|somatic|yoga|sounds|bilateral|tapping|meditations|sleep|quick-resets)/.test(p),
  },
  {
    to: "/learn",
    label: "Learn",
    icon: BookOpen,
    match: (p) => /^\/(learn|library|blog|resources|courses)/.test(p),
  },
  { to: "/community", label: "Community", icon: Users, match: (p) => /^\/(community|events|my-events)/.test(p) },
  {
    to: "/my-membership",
    label: "You",
    icon: UserRound,
    match: (p) => /^\/(my-membership|profile|favorites|preferences|journals|settings)/.test(p),
  },
];

/**
 * iOS-style tab bar. Rendered only inside the native app shell, so the
 * website's own navigation stays exactly as it is for web visitors.
 */
export function NativeTabBar() {
  const { pathname } = useLocation();

  return (
    <nav
      aria-label="Main"
      className="fixed inset-x-0 bottom-0 z-[80] border-t border-border/70 bg-background/95 backdrop-blur-xl"
      style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
    >
      <ul className="mx-auto flex max-w-lg items-stretch justify-between px-2">
        {TABS.map((tab) => {
          const active = tab.match(pathname);
          const Icon = tab.icon;
          return (
            <li key={tab.to} className="flex-1">
              <Link
                to={tab.to}
                onClick={() => hapticSelection()}
                aria-current={active ? "page" : undefined}
                aria-label={tab.label}
                className={`flex min-h-[52px] flex-col items-center justify-center gap-1 py-1.5 text-[10px] font-medium transition-colors ${
                  active ? "text-primary" : "text-muted-foreground"
                }`}
              >
                <Icon className="h-6 w-6" aria-hidden="true" />
                <span>{tab.label}</span>
              </Link>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
